import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import maskedBrandOption from '../JSON Files/MaskedBrandOption.json'
import ExceptionVariables from '../JSON Files/ExceptionVariables.json'
import getNotification from "../../Redux/Action/action";
import UserService from "../../services/UserService";

const { REACT_APP_UPLOAD_DATA } = process.env;
const { REACT_APP_REDIRECT_URI } = process.env;

const HIDDEN_CHART_VARIABLES = ExceptionVariables.hiddenroichart || [];

/** Chart rows name their variable under different keys depending on the API that produced them. */
export const isHiddenChartVariable = (row) =>
  [row?.variable, row?.variable_name, row?.media_variable_type, row?.variable_type]
    .some((name) => HIDDEN_CHART_VARIABLES.includes(name));

export const visibleChartRows = (rows) => (rows || []).filter((row) => !isHiddenChartVariable(row));

/** Shared JSON headers used by most tool-page axios configs */
export const defaultJsonHeaders = {
  Accept: "text/plain",
  "Content-Type": "application/json",
};

/**
 * Maps HTTP / axios failures to the same toast notifications used across tool pages.
 * Behavior matches the previous inline status switches (500/400/422/404/401/default).
 */
export function notifyRequestError(dispatch, err) {
  const status = err?.response?.status;
  const messageByStatus = {
    500: "Server is Down! Please try again after sometime",
    400: "Input is not in prescribed format",
    422: "Input is not in prescribed format",
    404: "Page not Found",
    401: "Session expired! Please log in again",
  };
  const message =
    messageByStatus[status] ||
    "Server is Down! Please try again after sometime";

  dispatch(
    getNotification({
      message,
      type: "default",
    })
  );
}

/** Round a chart top up just enough for the tallest bar to stay visible. */
export function niceAxisMax(highest) {
  const value = Number(highest);
  if (!Number.isFinite(value) || value <= 0) return 1;
  const padded = value * 1.25;
  const base = 10 ** Math.floor(Math.log10(padded));
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const niceFrac = steps.find((step) => padded / base <= step + 1e-9) ?? 10;
  return Number((niceFrac * base).toPrecision(12));
}

/** Title font for every Simulator / Optimizer chart (colour comes from .rr-apex in SCSS). */
export const CHART_TITLE_STYLE = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "15px",
  fontWeight: 700,
};

/** Non-zero pie slices are drawn at least this wide (%) so their colour stays visible. */
export const PIE_MIN_VISIBLE_PCT = 1.5;

/**
 * Real percentages (sum 100) plus the series to actually draw, where tiny slices are
 * widened to PIE_MIN_VISIBLE_PCT. Labels/legend/tooltip must read `percents`, not the chart's val.
 */
export const buildPieSeries = (values) => {
  const nums = (values || []).map((v) => Math.max(Number(v) || 0, 0));
  const total = nums.reduce((sum, v) => sum + v, 0);
  const percents = nums.map((v) => (total > 0 ? (v / total) * 100 : 0));
  const display = percents.map((p) => (p > 0 ? Math.max(p, PIE_MIN_VISIBLE_PCT) : 0));
  return { percents, display };
};

/** Slice label: none under 2% (legend shows it), "…" under 5%, otherwise the full %. */
export const pieSliceLabel = (pct, digits = 1) => {
  const n = Number(pct) || 0;
  if (n < 2) return "";
  if (n < 5) return "…";
  return `${n.toFixed(digits)}%`;
};

/** Legend entry with the slice's real % so tiny unlabelled slices stay readable. */
export const pieLegendWithValue = (name, pct, digits = 1) =>
  `${name}  ${(Number(pct) || 0).toFixed(digits)}%`;

/** Series colours shared by the Simulator bar charts. */
export const CHART_PALETTE = [
  "#17A2B8", "#FF8C00", "#0D7C66", "#DC3545", "#4A6274", "#C7F464", "#FFC107",
  "#20c997", "#DC3545", "#1A252F", "#6A0572", "#3D348B", "#198754", "#B8336A",
  "#1B998B", "#8F2D56", "#E63946", "#F3722C", "#FF8C00", "#90BE6D", "#198754",
  "#577590", "#F9844A", "#17A2B8", "#9D4EDD", "#5A189A",
];

/** Series colours shared by the Optimizer's own bar charts (…op.jsx). */
export const CHART_PALETTE_OP = [
  "#17A2B8", "#FF8C00", "#0D7C66", "#FF6B6B", "#556270", "#C7F464", "#FF9F1C",
  "#2EC4B6", "#E71D36", "#011627", "#6A0572", "#3D348B", "#43AA8B", "#B8336A",
  "#1B998B", "#8F2D56", "#F94144", "#F3722C", "#F8961E", "#90BE6D", "#43AA8B",
  "#577590", "#F9844A", "#277DA1", "#9D4EDD", "#5A189A",
];

export function formatAxisTick(val) {
  const num = Number(val);
  if (!Number.isFinite(num)) return "";
  if (num === 0) return "0";
  const digits = Math.abs(num) < 0.01 ? 4 : Math.abs(num) < 1 ? 3 : 0;
  return digits ? num.toFixed(digits) : num;
}

/** Driver values: GRP is shown as a whole number, every other unit with 2 decimals. */
export const driverDecimals = (units) =>
  String(units || "").trim().toUpperCase() === "GRP" ? 0 : 2;

/** GRP series draw as bars; everything else (INR, %, Impressions, counts…) as lines. */
export const isGrpSeries = (units, name) =>
  String(units || "").trim().toUpperCase() === "GRP" || /GRP/i.test(String(name || ""));

export function formatDriverValue(value, units, digits = driverDecimals(units)) {
  const num = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(num)) return value;
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Keeps a one-bar chart on the left by reserving empty slots to its right. */
export function alignSingleBarLeft(categories, series, slots = 6) {
  const labels = Array.isArray(categories) ? [...categories] : [];
  if (labels.length !== 1) {
    return { categories: labels, series: series || [] };
  }
  const pad = Math.max(slots - 1, 0);
  const paddedCategories = [
    ...labels,
    ...Array.from({ length: pad }, (_, index) => "\u200b".repeat(index + 1)),
  ];
  const paddedSeries = (series || []).map((item) => {
    const data = item?.data || [];
    const isPointSeries = data.some(
      (point) => point && typeof point === "object" && !Array.isArray(point)
    );
    if (isPointSeries) return item;
    return {
      ...item,
      data: [...data, ...Array(pad).fill(null)],
    };
  });
  return { categories: paddedCategories, series: paddedSeries };
}

export function hideBarPadLabel(value) {
  const text = Array.isArray(value) ? value.join(" ") : String(value ?? "");
  return text.includes("\u200b") ? "" : value;
}

/**
 * Keycloak login redirect used when a tool action runs while logged out.
 * @param {string} pathSuffix e.g. "/simulator" or "simulator"
 */
export function requireLogin(pathSuffix = "") {
  const path = !pathSuffix
    ? ""
    : pathSuffix.startsWith("/")
      ? pathSuffix
      : `/${pathSuffix}`;

  setTimeout(() => {
    UserService.doLogin({
      redirectUri: `${REACT_APP_REDIRECT_URI}${path}`,
    });
  }, 1000);
}

/**
 * Build react-select options from a list.
 * @param {Array} list
 * @param {string|Function} [getLabel] property name or mapper (default: item itself)
 * @param {string|Function} [getValue] property name or mapper (default: same as label)
 */
export function toSelectOptions(list, getLabel, getValue) {
  if (!Array.isArray(list)) return [];

  const resolve = (mapper, item) => {
    if (mapper == null) return item;
    if (typeof mapper === "string") return item?.[mapper];
    return mapper(item);
  };

  const valueMapper = getValue == null ? getLabel : getValue;

  return list.map((item) => ({
    label: resolve(getLabel, item),
    value: resolve(valueMapper, item),
  }));
}

/** Format a number as Indian Cr currency label (cockpit KPI cards). */
export function formatCr(value, { scaleToCr = true } = {}) {
  if (value === null || value === undefined || isNaN(value)) return "₹ 0 Cr";
  const n = scaleToCr ? Number(value) / 10000000 : Number(value);
  return `₹ ${n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} Cr`;
}

/** Bootstrap text class for YoY / growth values. */
export function getGrowthClass(value) {
  if (Number(value) > 0) return "text-success";
  if (Number(value) < 0) return "text-danger";
  return "text-secondary";
}

/** Previous/current FY labels with optional data-till month suffix. */
export function buildFyTillLabels(previousFY, currentFY, tilldate) {
  if (!tilldate) {
    return {
      previousTillLabel: previousFY,
      currentTillLabel: currentFY,
    };
  }

  const [year, month] = tilldate.split("-");
  return {
    previousTillLabel: `${previousFY} (${year}-${String(Number(month) - 1).padStart(2, "0")})`,
    currentTillLabel: `${currentFY} (${tilldate})`,
  };
}

/** Role → brand payload used by cockpit dashboardSlice.loadDashboardData. */
export function resolveDashboardBrandPayload(fy = "2025-26") {
  if (UserService.hasRole(["BBMNGR"])) {
    return { brand: "BAD BANGLES", fy };
  }
  if (UserService.hasRole(["OODMNGR"]) || UserService.hasRole(["SALES"])) {
    return { brand: "OODLES", fy };
  }
  if (UserService.hasRole(["CBMNGR"])) {
    return { brand: "CHERRY BRIGHT", fy };
  }
  if (UserService.hasRole(["RETAIL"])) {
    return { brand: "FASTRACK", fy };
  }
  return { brand: "MILD URGENCY", fy };
}

/**
 * Shared brand allow-list by role (tool pages + sessionSlice).
 * Expects brand objects with a `brand` field (get_brand_fy shape).
 * @param {Array<{brand: string}>} brands
 * @param {string[]} [hideList] brands to exclude (e.g. ExceptionVariables.brandoptionshide)
 */
export function filterBrandsByRole(brands = [], hideList = []) {
  const filtered = brands
    ?.filter((it) => !hideList?.includes(it?.brand))
    ?.sort((a, b) => a.brand.localeCompare(b.brand));

  if (UserService.hasRole(["BBMNGR"])) {
    return filtered?.filter((it) => it?.brand === "BAD BANGLES");
  }
  if (UserService.hasRole(["OODMNGR"]) || UserService.hasRole(["SALES"])) {
    return filtered?.filter((it) => it?.brand === "OODLES");
  }
  if (UserService.hasRole(["MUMNGR"])) {
    return filtered?.filter((it) => it?.brand === "MILD URGENCY");
  }
  if (UserService.hasRole(["CBMNGR"])) {
    return filtered?.filter((it) => it?.brand === "CHERRY BRIGHT");
  }
  if (UserService.hasRole(["RETAIL"])) {
    return filtered?.filter((it) => it?.brand === "FASTRACK");
  }
  return filtered;
}

/** Plot range label: "04-2025" → "Apr 2025" (Simulator / Optimizer / Saved Scenarios). */
export function formatPlotMonthYear(dateString) {
  if (!dateString) return "";
  const [month, year] = dateString.split("-");
  const date = new Date(`${year}-${month}-01`);
  return date.toLocaleString("default", { month: "short", year: "numeric" });
}

/** Timestamp used when saving/updating scenarios. */
export function getCurrentFormattedTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/** Brand abbreviation map for scenario naming. */
export const BRAND_ABB_DICT = {
  "YIPPEE NOODLES": "ND",
  "BINGO OS": "OS",
  "BINGO PC": "PC",
  "TEDHE MEDHE": "TN",
  "MARIE LIGHT": "ML",
  "DARK FANTASY": "DF",
  "MOMS MAGIC": "MM",
  "MAD ANGLES": "MA",
  BOUNCE: "BOUNCE",
  OODLES: "OOD",
  "BAD BANGLES": "BB",
};

export function getBrandAbb(brand) {
  return BRAND_ABB_DICT[brand] || brand;
}

/** Scenario name character validation (letters, numbers, space, underscore). */
export function isValidScenarioName(str) {
  return /^[A-Za-z0-9_ ]+$/.test(String(str || ""));
}

function tallest(datasets, valueOf) {
  const peaks = (datasets || []).map((rows) => {
    const items = (rows || []).filter((it) => Number.isFinite(valueOf(it)));
    const stacked = items.some((it) => it?.frequency);
    const totals = {};
    items.forEach((it, i) => {
      const key = stacked ? it.frequency : i;
      totals[key] = (totals[key] || 0) + valueOf(it);
    });
    return Math.max(0, ...Object.values(totals));
  });
  const peak = Math.max(0, ...peaks);
  return peak > 0 ? niceAxisMax(peak) : undefined;
}

/**
 * Shared Y-axis top for charts drawn side by side. Each argument is [rows, valueOf]
 * where valueOf returns the number exactly as that chart plots it.
 */
export function sharedAxisMax(...pairs) {
  const peaks = pairs
    .map(([rows, valueOf]) => tallest([visibleChartRows(rows)], (it) => Number(valueOf(it))))
    .filter((peak) => peak !== undefined);
  return peaks.length ? Math.max(...peaks) : undefined;
}

/** Shared Y-axis top for a left/right contribution pair (plot3 rows, value in Lacs, volume in Tonnes). */
export function contributionPairAxisMax(datasets, isValue) {
  return tallest(datasets, (it) => {
    if (!it) return NaN;
    const amount = it.type === "Core"
      ? (isValue ? it.total_core_sales : it.total_core)
      : it.type === "Incremental" || it.type === "Increment"
        ? (isValue ? it.total_incremental_sales : it.total_incremental)
        : (isValue ? it.total_media_sales : it.total_media);
    const n = Number(amount);
    if (!Number.isFinite(n)) return NaN;
    return isValue ? Number((n / 100000).toFixed(2)) : Number((n / 1000).toFixed(0));
  });
}

export const uploadPDF = async (input,filename,selectedBrand, reportType, setLoading, setDownloadUrl) => {
    try {
        setLoading(true); // disable button & show loader

        const canvas = await html2canvas(input, { scale: 3, useCORS: true, scrollY: -window.scrollY });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;
        }

        // Convert to Blob for upload
        const pdfBlob = pdf.output("blob");

        // const now = new Date();
        // const formattedDateTime = `${String(now.getDate()).padStart(2, "0")}-${String(
        //     now.getMonth() + 1
        // ).padStart(2, "0")}-${now.getFullYear()}_${String(now.getHours()).padStart(
        //     2,
        //     "0"
        // )}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;

        const pdfName = `${filename}.pdf`;

        // Upload to backend
        const formData = new FormData();
        formData.append("file", pdfBlob, pdfName);
        formData.append("report_type", reportType);
        formData.append("brand", maskedBrandOption.maskedBrandOption[selectedBrand]);
        formData.append("blob_name", pdfName);

        const response = await fetch(`${REACT_APP_UPLOAD_DATA}/app/uploadReport`, {
            method: "POST",
            body: formData,
        });

        const data = await response.json();

        if (response.ok) {
            setDownloadUrl(data.pdf_name); // show “Download PDF” button
        } else {
            alert("Failed to upload report");
        }
    } catch (error) {
        console.error("Error generating/uploading PDF:", error);
    } finally {
        setLoading(false);
    }
};

export const downloadPdf = async (job_id, close) => {
    try {
        const url = `${REACT_APP_UPLOAD_DATA}/app/downloadReport?job_id=${encodeURIComponent(job_id)}`;

        // Create a temporary <a> tag to trigger browser download directly
        const a = document.createElement("a");
        a.href = url;
        a.download = job_id.split("/").pop(); // only filename
        document.body.appendChild(a);
        a.click();
        a.remove();

        // Optionally, you can add a small delay to ensure loader is visible
        await new Promise((res) => setTimeout(res, 500));

    } catch (error) {
        console.error("Download failed:", error);
        alert("Failed to download PDF");
    } finally {
        close();
    }
};
